import {
	AlertCircle,
	ChevronDown,
	ChevronRight,
	Columns3,
	ExternalLink,
	Loader2,
	Rows3,
	Ticket,
} from "lucide-react";
import React, { useMemo } from "react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useUrlParam } from "../../hooks/useUrlParam";
import { groupByPrimaryTicket, jiraBrowseUrl } from "../../jiraTickets";
import { sortStackedMergeRequests } from "../../stackedMergeRequests";
import { ReviewBoardCard } from "../ReviewBoardCard";
import { LaneStats, collectOpenMRs, createSorter } from "./laneParts";

export const JIRA_LAYOUT = Object.freeze({
	COLUMNS: "columns",
	SWIMLANES: "swimlanes",
});

const NO_TICKET_LABEL = "No ticket";
// localStorage keys cannot hold null, so the unticketed lane collapses under
// a sentinel that no real ticket key can match (keys are uppercase).
const laneId = (lane) => lane.key ?? "__none__";

const CountPill = ({ count }) => (
	<span className="flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] text-[var(--color-text-secondary)] tabular-nums">
		{count}
	</span>
);

const LaneTitle = ({ lane, collapsed, onToggle }) => (
	<button
		type="button"
		onClick={onToggle}
		className="inline-flex items-center gap-2 min-w-0 font-semibold text-[var(--color-text-primary)] hover:text-[var(--color-text-secondary)] transition-colors"
		aria-expanded={!collapsed}
	>
		{collapsed ? (
			<ChevronRight className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]" />
		) : (
			<ChevronDown className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]" />
		)}
		<Ticket className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]" />
		<span
			className={`truncate ${lane.key ? "" : "font-medium text-[var(--color-text-secondary)]"}`}
		>
			{lane.key ?? NO_TICKET_LABEL}
		</span>
		<CountPill count={lane.mrs.length} />
	</button>
);

const JiraLink = ({ ticketKey }) =>
	ticketKey ? (
		<a
			href={jiraBrowseUrl(ticketKey)}
			target="_blank"
			rel="noopener noreferrer"
			className="p-1 rounded-md flex-shrink-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-secondary)] transition-colors"
			title={`Open ${ticketKey} in Jira`}
			aria-label={`Open ${ticketKey} in Jira`}
		>
			<ExternalLink className="w-4 h-4" />
		</a>
	) : null;

const LaneCard = ({ mr, cardProps }) => (
	<ReviewBoardCard mr={mr} enableDrag={false} {...cardProps} />
);

// Vertical column per ticket, wrapping across the page (like By Repository).
const TicketColumn = ({ lane, collapsed, onToggle, cardProps }) => {
	const label = lane.key ?? NO_TICKET_LABEL;

	if (collapsed) {
		return (
			<div className="flex flex-col flex-shrink-0 w-12">
				<button
					type="button"
					onClick={onToggle}
					className="flex flex-col items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50 py-2 h-full hover:bg-[var(--color-bg-secondary)] transition-colors"
					aria-expanded={false}
					title={label}
				>
					<ChevronRight className="w-4 h-4 text-[var(--color-text-secondary)]" />
					<CountPill count={lane.mrs.length} />
					<span
						className="text-xs font-semibold text-[var(--color-text-primary)] truncate max-h-[60vh]"
						style={{ writingMode: "vertical-rl", textOrientation: "mixed" }}
					>
						{label}
					</span>
				</button>
			</div>
		);
	}

	return (
		<div className="flex flex-col flex-shrink-0 w-96 max-h-[70vh]">
			<div className="rounded-t-lg border border-b-0 border-[var(--color-border)] bg-[var(--color-bg-secondary)]/50 px-3 py-2">
				<div className="flex items-center justify-between gap-2">
					<LaneTitle lane={lane} collapsed={false} onToggle={onToggle} />
					<JiraLink ticketKey={lane.key} />
				</div>
				<LaneStats mrs={lane.mrs} className="mt-2" />
			</div>
			<div className="flex-1 overflow-y-auto rounded-b-lg border border-t-0 border-[var(--color-border)] bg-[var(--color-bg-primary)] p-2">
				{lane.mrs.map((mr) => (
					<LaneCard key={mr.id} mr={mr} cardProps={cardProps} />
				))}
			</div>
		</div>
	);
};

// Full-width row per ticket with its MRs side by side, scrolling sideways.
const TicketSwimlane = ({ lane, collapsed, onToggle, cardProps }) => (
	<section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-primary)]">
		<div
			className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-[var(--color-bg-secondary)]/50 px-3 py-2 ${
				collapsed
					? "rounded-lg"
					: "rounded-t-lg border-b border-[var(--color-border)]"
			}`}
		>
			<LaneTitle lane={lane} collapsed={collapsed} onToggle={onToggle} />
			<LaneStats mrs={lane.mrs} />
			<div className="ml-auto">
				<JiraLink ticketKey={lane.key} />
			</div>
		</div>
		{!collapsed && (
			<div className="flex items-start gap-3 overflow-x-auto p-2 pb-0">
				{lane.mrs.map((mr) => (
					<div key={mr.id} className="w-80 flex-shrink-0">
						<LaneCard mr={mr} cardProps={cardProps} />
					</div>
				))}
			</div>
		)}
	</section>
);

const LAYOUT_OPTIONS = [
	{ layout: JIRA_LAYOUT.COLUMNS, label: "Columns", icon: Columns3 },
	{ layout: JIRA_LAYOUT.SWIMLANES, label: "Swimlanes", icon: Rows3 },
];

const LayoutToggle = ({ layout, onChange }) => (
	<fieldset className="flex items-center rounded-lg bg-[var(--color-bg-primary)] p-0.5 ring-1 ring-[var(--color-border)]">
		<legend className="sr-only">Layout</legend>
		{LAYOUT_OPTIONS.map(({ layout: value, label, icon: Icon }) => {
			const selected = layout === value;
			return (
				<button
					key={value}
					type="button"
					onClick={() => onChange(value)}
					aria-pressed={selected}
					className={`inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-medium active:scale-[0.96] transition-[background-color,color,transform] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] ${
						selected
							? "bg-[var(--color-bg-secondary)] text-[var(--color-text-primary)] shadow-sm"
							: "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
					}`}
				>
					<Icon className="h-3.5 w-3.5" />
					{label}
				</button>
			);
		})}
	</fieldset>
);

/**
 * Groups open merge requests by their primary Jira ticket (the key each card's
 * badge shows), as vertical columns or full-width swimlanes. MRs that reference
 * no ticket collect in a final "No ticket" lane.
 */
export const JiraView = ({
	pendingReviewMRs,
	myOpenMRs,
	loading,
	error,
	sortOption = "updatedAt",
	greyOutBlockingComments = true,
	enableCommentsExpansion = false,
}) => {
	// The stored layout is the viewer's default; `?layout=` overrides it so a
	// link can open a specific layout. Toggling updates both.
	const [storedLayout, setStoredLayout] = useLocalStorage(
		"jiraview-layout",
		JIRA_LAYOUT.COLUMNS,
	);
	const [urlLayout, setUrlLayout] = useUrlParam("layout");
	const layout = Object.values(JIRA_LAYOUT).includes(urlLayout)
		? urlLayout
		: storedLayout;
	const setLayout = (next) => {
		setStoredLayout(next);
		setUrlLayout(next);
	};
	const [collapsedLanes, setCollapsedLanes] = useLocalStorage(
		"jiraview-collapsed",
		{},
	);

	const lanes = useMemo(() => {
		const sorter = createSorter(sortOption);
		return groupByPrimaryTicket(
			collectOpenMRs(pendingReviewMRs, myOpenMRs),
		).map((lane) => ({
			...lane,
			mrs: sortStackedMergeRequests(lane.mrs, sorter),
		}));
	}, [pendingReviewMRs, myOpenMRs, sortOption]);

	const toggleLane = (id) => {
		setCollapsedLanes((prev) => ({ ...prev, [id]: !prev[id] }));
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-[calc(100vh-200px)]">
				<div className="text-center">
					<Loader2 className="w-12 h-12 mx-auto text-[var(--color-text-secondary)] animate-spin mb-4" />
					<p className="text-[var(--color-text-secondary)]">
						Loading merge requests...
					</p>
				</div>
			</div>
		);
	}

	const cardProps = { greyOutBlockingComments, enableCommentsExpansion };
	const Lane = layout === JIRA_LAYOUT.SWIMLANES ? TicketSwimlane : TicketColumn;
	const ticketCount = lanes.filter((lane) => lane.key).length;

	return (
		<div className="w-full">
			{error && (
				<div className="mb-4 flex items-center gap-2 rounded-lg bg-[var(--color-error-bg)] p-3 text-[var(--color-error-text)] ring-1 ring-[var(--color-error-border)]">
					<AlertCircle className="w-5 h-5 flex-shrink-0" />
					<span className="text-sm">{error}</span>
				</div>
			)}

			<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
				<p className="text-sm text-[var(--color-text-secondary)] tabular-nums">
					{ticketCount} {ticketCount === 1 ? "ticket" : "tickets"}
				</p>
				<LayoutToggle layout={layout} onChange={setLayout} />
			</div>

			{lanes.length === 0 ? (
				<div className="bg-[var(--color-info-bg)] text-[var(--color-info-text)] px-4 py-3 rounded">
					No open merge requests
				</div>
			) : (
				<div
					className={
						layout === JIRA_LAYOUT.SWIMLANES
							? "flex flex-col gap-3 pb-4"
							: "flex flex-wrap gap-4 pb-4 items-start"
					}
				>
					{lanes.map((lane) => (
						<Lane
							key={laneId(lane)}
							lane={lane}
							collapsed={!!collapsedLanes[laneId(lane)]}
							onToggle={() => toggleLane(laneId(lane))}
							cardProps={cardProps}
						/>
					))}
				</div>
			)}
		</div>
	);
};
